import React, { useState, useEffect } from 'react';
import {
  Box,
  Avatar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  Divider,
  Chip,
} from '@mui/material';

import {
  Person,
  Settings,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Bed,
  ChevronRight,
   Assignment as AssignmentIcon,
} from '@mui/icons-material';
import ScienceIcon from '@mui/icons-material/Science';
import AnimationIcon from '@mui/icons-material/Animation';
import AssistWalkerIcon from '@mui/icons-material/AssistWalker';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BackspaceIcon from '@mui/icons-material/Backspace';
import Department from './AdminPanel/Department';
import SystemUser from './AdminPanel/SystemUser';
import Role from './AdminPanel/Role';
import Insurance from './AdminPanel/Insurance';
import StaffRegistation from './AdminPanel/StaffRegistation';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SwitchAccessShortcutAddIcon from '@mui/icons-material/SwitchAccessShortcutAdd';
import Access from './AdminPanel/Access';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import ComponentAccess from './AdminPanel/ComponentAccess';
import ExtensionIcon from '@mui/icons-material/Extension';
import WardsBeds from './AdminPanel/WardsBeds';
import ServicesSetting from './AdminPanel/ServicesSetting';
import Diagnosis from './AdminPanel/Diagnosis';
import Procedure from './AdminPanel/Procedure';
import Laboratory from './AdminPanel/Laboratory';
import Scan from './AdminPanel/Scan';
import SmsMessage from './AdminPanel/SmsMessage';
import SmsIcon from '@mui/icons-material/Sms';
import HospitalAdminSettings from './AdminPanel/Hospitaladminsettings';
import { Import } from 'lucide-react';
import ApproveRequestWriteoff from './AdminPanel/ApproveRequestWriteoff';
import PolicySOPManagement from './AdminPanel/PolicySOPManagement';
import NurseWardAssignment from './AdminPanel/NurseWardAssignment';
import AuditLog from './AdminPanel/AuditLog';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import SubBranches from './AdminPanel/SubBranches';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import axios from 'axios';
import { branchesList } from './BaseURL';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [userInfo, setUserInfo] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sub-branches are hidden from the menu when the facility runs only the
  // main branch. System Administrators still see the entry so they can
  // create the first sub-branch when they're ready to.
  const [branchCount, setBranchCount] = useState(1);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Primary brand color from uploaded image
  const primaryColor = '#0e926a';
  const primaryLight = '#10b080';
  const primaryDark = '#0a7554';

  useEffect(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) setUserInfo(JSON.parse(raw));
    } catch (_) {}
    (async () => {
      try {
        const { data } = await axios.get(branchesList, { timeout: 8000 });
        if (data?.success) setBranchCount((data.results || []).length);
      } catch (_) { /* leave default */ }
    })();
  }, []);

  useEffect(() => {
    if (userInfo && userInfo.is_staff === 0) {
      // Handle non-staff user
    }
  }, [userInfo]);

  const showBranchAdmin = branchCount > 1 || userInfo?.role === 'System Administrator';

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Sidebar layout — grouped into the three sections requested:
  //   Insurance     → insurance company config
  //   Administrative → system, users, departments, clinical setup, Ward Assignments
  //   Accounting    → financial control (write-offs, etc.)
  // Each item still has a unique numeric id so activeTab logic is unchanged.
  const menuGroups = [
    {
      title: 'Insurance',
      items: [
        { id: 7,  label: 'Insurance Companies',     icon: <AddBusinessIcon />,             component: <Insurance /> },
      ],
    },
    {
      title: 'Administrative',
      items: [
        { id: 6,  label: 'System Settings',         icon: <Settings />,                    component: <HospitalAdminSettings /> },
        { id: 18, label: 'Audit Trail',             icon: <HistoryEduIcon />,              component: <AuditLog /> },
        ...(showBranchAdmin
          ? [{ id: 19, label: 'Sub-Branches',       icon: <AccountTreeIcon />,             component: <SubBranches /> }]
          : []),
        { id: 0,  label: 'System Departments',      icon: <Person />,                      component: <Department /> },
        { id: 1,  label: 'Policy / SOP Management', icon: <AssignmentLateIcon />,          component: <PolicySOPManagement /> },
        { id: 2,  label: 'User Roles',              icon: <LowPriorityIcon />,             component: <Role /> },
        { id: 3,  label: 'System Users',            icon: <GroupAddIcon />,                component: <SystemUser /> },
        { id: 4,  label: 'Staff Registration',      icon: <PersonAddIcon />,               component: <StaffRegistation /> },
        { id: 5,  label: 'Department Access',       icon: <SwitchAccessShortcutAddIcon />, component: <Access /> },
        { id: 8,  label: 'Component Access',        icon: <ExtensionIcon />,               component: <ComponentAccess /> },
        { id: 9,  label: 'Wards & Beds',            icon: <Bed />,                         component: <WardsBeds /> },
        { id: 17, label: 'Ward Assignments',        icon: <AssignmentIcon />,              component: <NurseWardAssignment /> },
        { id: 10, label: 'Services Setup',          icon: <AssignmentIcon />,              component: <ServicesSetting /> },
        { id: 12, label: 'Diagnosis Setup',         icon: <AssistWalkerIcon />,            component: <Diagnosis /> },
        { id: 13, label: 'Procedure Setup',         icon: <AssignmentLateIcon />,          component: <Procedure /> },
        { id: 14, label: 'Laboratory Setup',        icon: <ScienceIcon />,                 component: <Laboratory /> },
        { id: 15, label: 'Scan Setup',              icon: <AnimationIcon />,               component: <Scan /> },
        { id: 16, label: 'SMS Messaging',           icon: <SmsIcon />,                     component: <SmsMessage /> },
      ],
    },
    {
      title: 'Accounting',
      items: [
        { id: 11, label: 'Write-off Approvals',     icon: <BackspaceIcon />,               component: <ApproveRequestWriteoff /> },
      ],
    },
  ];

  // Flat list for activeTab → component lookup.
  const menuItems = menuGroups.flatMap(g => g.items);

  // Sidebar content
  const sidebarContent = (
    <Box 
      sx={{ 
        width: { xs: 280, md: 300 }, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 3, 
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
          color: 'white',
          boxShadow: `0 4px 20px ${primaryColor}40`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <DashboardIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
              Admin Panel
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              System Management
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider />

      {/* Menu — grouped sections with subtle headers */}
      <Box sx={{ flexGrow: 1, py: 1, overflowY: 'auto' }}>
        {menuGroups.map((group, gi) => (
          <Box key={group.title} sx={{ mb: gi === menuGroups.length - 1 ? 0 : 1 }}>
            {/* Section header */}
            <Typography sx={{
              px: 3, pt: gi === 0 ? 1.5 : 2, pb: 0.5,
              fontSize: '0.66rem', fontWeight: 800,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              color: '#94a3b8',
            }}>
              {group.title}
            </Typography>

            <List dense disablePadding sx={{ px: 1.5 }}>
              {group.items.map((item) => {
                const selected = activeTab === item.id;
                return (
                  <ListItemButton
                    key={item.id}
                    selected={selected}
                    onClick={(e) => handleTabChange(e, item.id)}
                    sx={{
                      borderRadius: 1.5,
                      mb: 0.25,
                      py: 0.6,
                      pl: 1.25,
                      transition: 'background-color .15s ease, color .15s ease',
                      position: 'relative',
                      // teal stripe on the left when active
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0, top: '20%', bottom: '20%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        bgcolor: selected ? primaryColor : 'transparent',
                      },
                      '&:hover': { bgcolor: `${primaryColor}10` },
                      '&.Mui-selected': {
                        bgcolor: `${primaryColor}14`,
                        '&:hover': { bgcolor: `${primaryColor}1F` },
                        '& .MuiListItemIcon-root': { color: primaryColor },
                        '& .MuiListItemText-primary': { color: primaryColor, fontWeight: 700 },
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 32,
                      color: selected ? primaryColor : '#64748b',
                      '& svg': { fontSize: 18 },
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: selected ? 700 : 500,
                        fontSize: '0.84rem',
                        color: selected ? primaryColor : '#334155',
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider',
          bgcolor: `${primaryColor}08`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Settings sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            System Settings
          </Typography>
        </Box>
        <Chip 
          label="v2.0.1" 
          size="small" 
          sx={{ 
            fontSize: '0.7rem',
            height: 20,
            bgcolor: `${primaryColor}20`,
            color: primaryColor,
            fontWeight: 600,
          }} 
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: 300,
            borderRight: 1,
            borderColor: 'divider',
            borderRadius: 0,
            height: '100vh',
            position: 'sticky',
            top: 0,
            boxShadow: '2px 0 12px rgba(0, 0, 0, 0.05)',
          }}
        >
          {sidebarContent}
        </Paper>
      )}

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Mobile Header */}
        {isMobile && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {menuItems.find(i => i.id === activeTab)?.label}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Admin Panel
                </Typography>
              </Box>
              <IconButton 
                onClick={() => setMobileOpen(true)}
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Paper>
        )}

        {/* Content Area */}
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
          {/* Page Header for Desktop */}
          {!isMobile && (
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5,
                }}
              >
                {menuItems.find(i => i.id === activeTab)?.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and configure your system settings
              </Typography>
            </Box>
          )}

          {/* Dynamic Content with Fade Animation */}
          <Box
            sx={{
              animation: 'fadeIn 0.4s ease',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              }
            }}
          >
            {menuItems.find(i => i.id === activeTab)?.component}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPanel;